import { defineMessages, MessageDescriptor } from 'react-intl';
import { State, useStoreState } from 'easy-peasy';
import type { StoreModel } from '~/store/StoreModel';

// A solution owner holding only buyer divisions (a buyer-owned solution such
// as Hershey's) is linked to suppliers, so its customer screens call them
// that. Owners on the seller side, or on both sides, keep "customers".
export const useCounterparty = () =>
  useStoreState(
    ({ auth }: State<StoreModel>) =>
      auth.isSolutionOwner &&
      !!auth.solutionDivisions?.length &&
      auth.solutionDivisions.every((d) => d?.divisiontype === 'buyer')
  )
    ? 'supplier'
    : 'customer';

const customer = defineMessages({
  plural: { id: 'customers', defaultMessage: 'Customers' },
  singular: { id: 'customer', defaultMessage: 'Customer' },
  manage: { id: 'manage-customers', defaultMessage: 'Manage customers' },
  nameHeader: {
    id: 'customer-name-header',
    defaultMessage: 'Customer {customerName}',
  },
  backToList: {
    id: 'back-to-customers-list',
    defaultMessage: 'Back to the list of customers',
  },
  edit: { id: 'edit-customer', defaultMessage: 'Edit customer' },
  view: { id: 'view-customer', defaultMessage: 'View customer' },
  viewDetails: {
    id: 'view-customer-details',
    defaultMessage: 'View customer details',
  },
  search: { id: 'search-customers', defaultMessage: 'Search customers' },
  noneToDisplay: {
    id: 'no-customers-to-display',
    defaultMessage: 'There are currently no customers to display',
  },
  detailsHeader: {
    id: 'customerdetails-header',
    defaultMessage: 'Customer details',
  },
  detailsSaved: {
    id: 'customer-details-saved',
    defaultMessage: 'Customers details are saved',
  },
  detailsNotSaved: {
    id: 'customer-details-not-saved',
    defaultMessage: 'Customers details are NOT saved',
  },
  fetchError: {
    id: 'error-fetching-customer-details',
    defaultMessage:
      'Something went wrong while fetching customer details. Please try again.',
  },
  accessDescription: {
    id: 'manage-customer-access',
    defaultMessage: 'Manage whether this customer can access your solution',
  },
  divisionDescription: {
    id: 'select-division-customer-linked',
    defaultMessage: 'Select the division this customer is linked with',
  },
  settingsLoadFailed: {
    id: 'customer-settings-load-failed',
    defaultMessage: 'Could not load customer settings. Please try again.',
  },
  enabled: { id: 'customer-enabled', defaultMessage: 'Customer enabled' },
  disabled: { id: 'customer-disabled', defaultMessage: 'Customer disabled' },
  accessNotUpdated: {
    id: 'customer-access-not-updated',
    defaultMessage: 'Customer access could not be updated',
  },
  accessTooltip: {
    id: 'solution-access-tooltip',
    defaultMessage:
      "Customer can view your solution and ask questions. An enabled customer's approval to trade can be managed separately.",
  },
  disable: { id: 'disable-customer', defaultMessage: 'Disable customer' },
  disableBody: {
    id: 'disable-customer-body',
    defaultMessage:
      'Are you sure you want to disable this customer? Once disabled, the customer will not be able to access the division/solution pages.',
  },
  productNone: {
    id: 'no-customers-to-display',
    defaultMessage: 'No customers to display.',
  },
  selectTitle: { id: 'select-customers', defaultMessage: 'Select customers' },
  selectDescription: {
    id: 'select-customers-description',
    defaultMessage:
      'Select which customers can buy this product as well as the applicable incoterm',
  },
  all: { id: 'all-customers', defaultMessage: 'All customers' },
  prefsSaved: {
    id: 'customer-preferences-saved',
    defaultMessage: 'Customer preferences are saved',
  },
  prefsSaveError: {
    id: 'customer-preferences-save-error',
    defaultMessage: 'Could not save customer preferences',
  },
  added: {
    id: 'customer-added-to-list',
    defaultMessage: 'Customer added to the list',
  },
  addError: {
    id: 'customer-added-to-list-error',
    defaultMessage: 'Could not add the customer to the list',
  },
  removed: {
    id: 'customer-removed-from-list',
    defaultMessage: 'Customer removed from the list',
  },
  removeError: {
    id: 'customer-removed-from-list-error',
    defaultMessage: 'Could not remove the customer from the list',
  },
  editProductTitle: {
    id: 'edit-product-customers',
    defaultMessage: 'Edit product {name} - Customers',
  },
});

const supplier: Record<keyof typeof customer, MessageDescriptor> =
  defineMessages({
    plural: { id: 'suppliers', defaultMessage: 'Suppliers' },
    singular: { id: 'supplier', defaultMessage: 'Supplier' },
    manage: { id: 'manage-suppliers', defaultMessage: 'Manage suppliers' },
    nameHeader: {
      id: 'supplier-name-header',
      defaultMessage: 'Supplier {customerName}',
    },
    backToList: {
      id: 'back-to-suppliers-list',
      defaultMessage: 'Back to the list of suppliers',
    },
    edit: { id: 'edit-supplier', defaultMessage: 'Edit supplier' },
    view: { id: 'view-supplier', defaultMessage: 'View supplier' },
    viewDetails: {
      id: 'view-supplier-details',
      defaultMessage: 'View supplier details',
    },
    search: { id: 'search-suppliers', defaultMessage: 'Search suppliers' },
    noneToDisplay: {
      id: 'no-suppliers-to-display',
      defaultMessage: 'There are currently no suppliers to display',
    },
    detailsHeader: {
      id: 'supplierdetails-header',
      defaultMessage: 'Supplier details',
    },
    detailsSaved: {
      id: 'supplier-details-saved',
      defaultMessage: 'Supplier details are saved',
    },
    detailsNotSaved: {
      id: 'supplier-details-not-saved',
      defaultMessage: 'Supplier details are NOT saved',
    },
    fetchError: {
      id: 'error-fetching-supplier-details',
      defaultMessage:
        'Something went wrong while fetching supplier details. Please try again.',
    },
    accessDescription: {
      id: 'manage-supplier-access',
      defaultMessage: 'Manage whether this supplier can access your solution',
    },
    divisionDescription: {
      id: 'select-division-supplier-linked',
      defaultMessage: 'Select the division this supplier is linked with',
    },
    settingsLoadFailed: {
      id: 'supplier-settings-load-failed',
      defaultMessage: 'Could not load supplier settings. Please try again.',
    },
    enabled: { id: 'supplier-enabled', defaultMessage: 'Supplier enabled' },
    disabled: { id: 'supplier-disabled', defaultMessage: 'Supplier disabled' },
    accessNotUpdated: {
      id: 'supplier-access-not-updated',
      defaultMessage: 'Supplier access could not be updated',
    },
    accessTooltip: {
      id: 'supplier-solution-access-tooltip',
      defaultMessage:
        "Supplier can view your solution and ask questions. An enabled supplier's approval to trade can be managed separately.",
    },
    disable: { id: 'disable-supplier', defaultMessage: 'Disable supplier' },
    disableBody: {
      id: 'disable-supplier-body',
      defaultMessage:
        'Are you sure you want to disable this supplier? Once disabled, the supplier will not be able to access the division/solution pages.',
    },
    productNone: {
      id: 'no-suppliers-to-display-for-product',
      defaultMessage: 'No suppliers to display.',
    },
    selectTitle: { id: 'select-suppliers', defaultMessage: 'Select suppliers' },
    selectDescription: {
      id: 'select-suppliers-description',
      defaultMessage:
        'Select which suppliers can sell this product as well as the applicable incoterm',
    },
    all: { id: 'all-suppliers', defaultMessage: 'All suppliers' },
    prefsSaved: {
      id: 'supplier-preferences-saved',
      defaultMessage: 'Supplier preferences are saved',
    },
    prefsSaveError: {
      id: 'supplier-preferences-save-error',
      defaultMessage: 'Could not save supplier preferences',
    },
    added: {
      id: 'supplier-added-to-list',
      defaultMessage: 'Supplier added to the list',
    },
    addError: {
      id: 'supplier-added-to-list-error',
      defaultMessage: 'Could not add the supplier to the list',
    },
    removed: {
      id: 'supplier-removed-from-list',
      defaultMessage: 'Supplier removed from the list',
    },
    removeError: {
      id: 'supplier-removed-from-list-error',
      defaultMessage: 'Could not remove the supplier from the list',
    },
    editProductTitle: {
      id: 'edit-product-suppliers',
      defaultMessage: 'Edit product {name} - Suppliers',
    },
  });

const MESSAGES = { customer, supplier };

export const useCounterpartyMessages = () => MESSAGES[useCounterparty()];
